import * as React from 'react'
import { useOutletContext } from 'react-router-dom'

import { F0Dialog } from '@factorialco/f0-react'
import {
  AddEmployeesToApprovalGroupDocument,
  ApprovalsGroupFragment,
} from 'generated/resources/graphql'
import isNil from 'lodash/isNil'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

interface Props {
  onClose: () => void
  approvalGroupId: string
}

const AddEmployeesModal = ({ onClose, approvalGroupId }: Props) => {
  const { group } = useOutletContext<{ group: ApprovalsGroupFragment }>()

  const excludedEmployeeIds = React.useMemo(
    () =>
      group.groupMemberships
        .map((m) => m.access?.employee?.id)
        .filter((id): id is string => !isNil(id)),
    [group.groupMemberships]
  )

  const [employeeSelection, setEmployeeSelection] =
    React.useState<EmployeeSelectorV2MultiSelectState>({
      type: 'manual',
      ids: [],
    })

  const [addEmployeesToGroup, { loading: running }] = useMutation(
    AddEmployeesToApprovalGroupDocument,
    {
      extractorFn: (data) => data.approvalsMutations.bulkAssignApprovalsApprovalGroupMembership,
      refetchQueries: ['ApprovalGroupEmployeesDataTable'],
    }
  )

  const hasSelection =
    employeeSelection.type === 'all' ||
    (employeeSelection.type === 'manual' && employeeSelection.ids.length > 0)

  const handleSubmit = async () => {
    if (!hasSelection) return

    const result = await addEmployeesToGroup({
      variables: {
        employeeSelection,
        approvalGroupId,
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      onClose()
    }

    return handled
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      title={i18n.t('settings.approvals.approval_group.employees.add_employees.title_modal')}
      description={i18n.t('settings.approvals.approval_group.employees.add_employees.description')}
      onClose={onClose}
      primaryAction={{
        label: i18n.t('settings.approvals.approval_group.employees.add_employees.title'),
        onClick: handleSubmit,
        disabled: !hasSelection || running,
        loading: running,
      }}
    >
      <EmployeeSelectorV2
        multiple
        value={employeeSelection.type === 'manual' ? employeeSelection.ids : []}
        onChange={setEmployeeSelection}
        forcedFilters={{ excludeIds: excludedEmployeeIds, verb: 'ViewApprovalStepApprovers' }}
        options={{ asList: true, hideLabel: true }}
        hint={i18n.t('settings.approvals.approval_group.employees.add_employees.warning')}
      />
    </F0Dialog>
  )
}
export default AddEmployeesModal
